import {
  addYears,
  getDaysInMonth,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  setDate,
  setMonth,
  setYear,
  subYears,
} from 'date-fns';
import { FC, useMemo, useState } from 'react';

import Button from '../../Button';
import { StyledWheelActiveRow, StyledWheelGroup, WheelColumn } from '../../Wheel';
import ChevronDownIcon from '../assets/chevron-down.svg';
import { StyledSetDateToggle } from '../DatePickerNavHeader.styled';
import { StyledContainer } from './SetDatePicker.styled';

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const DEFAULT_YEARS_BACK = 100;
const DEFAULT_YEARS_FORWARD = 10;

export interface SetDatePickerProps {
  value: Date | null;
  minDate?: Date;
  maxDate?: Date;
  onBack: () => void;
  onConfirm: (date: Date) => void;
}

const clampDate = (date: Date, minDate?: Date, maxDate?: Date) => {
  if (minDate && isBefore(date, minDate)) return minDate;
  if (maxDate && isAfter(date, maxDate)) return maxDate;
  return date;
};

const SetDatePicker: FC<SetDatePickerProps> = ({ value, minDate, maxDate, onBack, onConfirm }) => {
  const initial = value ?? new Date();

  const [year, setYearState] = useState(() => getYear(initial));
  const [month, setMonthState] = useState(() => getMonth(initial));
  const [day, setDayState] = useState(() => initial.getDate());

  const minYear = minDate ? getYear(minDate) : getYear(subYears(new Date(), DEFAULT_YEARS_BACK));
  const maxYear = maxDate ? getYear(maxDate) : getYear(addYears(new Date(), DEFAULT_YEARS_FORWARD));

  const years = useMemo(
    () => Array.from({ length: maxYear - minYear + 1 }, (_, i) => String(minYear + i)),
    [minYear, maxYear],
  );

  const daysInMonth = getDaysInMonth(new Date(year, month));
  const days = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => String(i + 1)),
    [daysInMonth],
  );
  const clampedDay = Math.min(day, daysInMonth);

  const clampParts = (nextYear: number, nextMonth: number, nextDay: number) => {
    const nextDaysInMonth = getDaysInMonth(new Date(nextYear, nextMonth));
    const composed = clampDate(
      setDate(setMonth(setYear(initial, nextYear), nextMonth), Math.min(nextDay, nextDaysInMonth)),
      minDate,
      maxDate,
    );
    return { year: getYear(composed), month: getMonth(composed), day: composed.getDate() };
  };

  const applyClamped = (nextYear: number, nextMonth: number, nextDay: number) => {
    const clamped = clampParts(nextYear, nextMonth, nextDay);
    setYearState(clamped.year);
    setMonthState(clamped.month);
    setDayState(clamped.day);
    return clamped;
  };

  const handleConfirm = () => {
    onConfirm(setDate(setMonth(setYear(initial, year), month), clampedDay));
  };

  return (
    <StyledContainer>
      <StyledSetDateToggle type="button" onClick={onBack}>
        Set Date
        <ChevronDownIcon />
      </StyledSetDateToggle>

      <StyledWheelGroup>
        <StyledWheelActiveRow />
        <WheelColumn
          items={MONTH_ABBREVIATIONS}
          selectedIndex={month}
          onSelect={(index) => applyClamped(year, index, day).month}
          aria-label="Month"
        />
        <WheelColumn
          items={days}
          selectedIndex={clampedDay - 1}
          onSelect={(index) => applyClamped(year, month, index + 1).day - 1}
          aria-label="Day"
        />
        <WheelColumn
          items={years}
          selectedIndex={year - minYear}
          onSelect={(index) => applyClamped(minYear + index, month, day).year - minYear}
          aria-label="Year"
        />
      </StyledWheelGroup>

      <Button color="primary" variant="solid" size="md" fullWidth onClick={handleConfirm}>
        Confirm
      </Button>
    </StyledContainer>
  );
};

SetDatePicker.displayName = 'SetDatePicker';

export default SetDatePicker;
